import React from 'react';
import { Image } from '@chakra-ui/react';
import {
  ErrorMessage,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { MeetingTypePickerSkeleton } from './MeetingTypePickerSkeleton';
import { MeetingTypeButton } from './MeetingTypeButton';
import { ProviderType } from '../../api/types';
import { MeetingType } from '../../models/meeting';

const clipboardSvgUri = '/clipboard.svg';

type Props = {
  hasCoachCallScheduled: boolean;
  hasProviderCallScheduled: boolean;
  isError: boolean;
  isLoading: boolean;
  nextCoachMeetingType: MeetingType;
  nextProviderMeetingType: MeetingType;
  onSelectMeetingType: (
    providerType: ProviderType,
    meetingType: MeetingType
  ) => void;
  showCollaborativeCareMeetings: boolean;
};

export const MeetingTypePicker = ({
  hasCoachCallScheduled,
  hasProviderCallScheduled,
  isError,
  isLoading,
  nextCoachMeetingType,
  nextProviderMeetingType,
  onSelectMeetingType,
  showCollaborativeCareMeetings,
}: Props) => {
  if (isError) {
    return (
      <ErrorMessage
        message={
          'Sorry, an error occurred while trying to retrieve the data. Please try again or contact support.'
        }
      />
    );
  }

  return (
    <VStack>
      <HStack alignItems={'center'} justifyContent={'space-between'} mb={8}>
        <Text color={'blueGray.600'} fontSize={'sm'} fontWeight={'normal'}>
          Step 1: Select Visit Type
        </Text>
      </HStack>

      {isLoading && <MeetingTypePickerSkeleton />}

      {!isLoading && (
        <HStack>
          <HStack flex={3} gap={3}>
            {showCollaborativeCareMeetings && (
              <VStack flex={1} gap={3}>
                <MeetingTypeButton
                  disabled={hasProviderCallScheduled}
                  onClick={() => {
                    onSelectMeetingType(
                      ProviderType.Provider,
                      nextProviderMeetingType
                    );
                  }}
                  subText={nextProviderMeetingType}
                  text={'Medical Provider'}
                />

                <MeetingTypeButton
                  onClick={() => {
                    onSelectMeetingType(
                      ProviderType.Pharmacist,
                      MeetingType.FollowUpSession
                    );
                  }}
                  subText={MeetingType.Consult}
                  text={'Pharmacist'}
                />
              </VStack>
            )}

            <VStack flex={1} gap={3}>
              <MeetingTypeButton
                disabled={hasCoachCallScheduled}
                onClick={() => {
                  onSelectMeetingType(ProviderType.Coach, nextCoachMeetingType);
                }}
                subText={nextCoachMeetingType}
                text={'Coach'}
              />

              {showCollaborativeCareMeetings && (
                <MeetingTypeButton
                  onClick={() => {
                    onSelectMeetingType(
                      ProviderType.Dietitian,
                      MeetingType.FollowUpSession
                    );
                  }}
                  subText={MeetingType.Consult}
                  text={'Dietitian'}
                />
              )}
            </VStack>
          </HStack>

          <HStack flex={{ base: 0, lg: 2 }} position={'relative'}>
            <Image
              alt={'clipboard icon'}
              bottom={'-32px'}
              height={'215px'}
              position={'absolute'}
              right={'-48px'}
              src={clipboardSvgUri}
              width={'310px'}
            />
          </HStack>
        </HStack>
      )}
    </VStack>
  );
};
